import { type FC, useMemo } from "react";
import { Col, Container, HeadingLevel, Layout, Row } from "@viagogo/core/ui";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import { GridType, type VenueEventListViewModel } from "src/http/types";
import { FormatContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, useLocationContext } from "src/shared/context/location-context";
import { FilterTypes } from "src/shared/enums/enums";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation";
import type { Dates } from "src/types/shared/cookies";
import { LinksGridManager } from "src/website-apps/responsive/stubhub-venue/components/LinksGridManager";
import { useVenuePageModelData } from "src/website-apps/responsive/stubhub-venue/hooks/useVenuePageModelData";
import { NewImmersiveHeaderWrapper } from "src/website-apps/shared/components/ancillary-pages/NewImmersiveHeaderWrapper";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { ErrorMessage } from "src/website-apps/shared/components/ErrorMessage";
import { EventListRecentlyViewedGrid } from "src/website-apps/shared/components/event-list-recently-viewed-grid";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { LazyFooter } from "src/website-apps/shared/components/footer";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeaderConfiguration } from "src/website-apps/shared/components/header";
import { HeroBannerFavoritesDataContext } from "src/website-apps/shared/components/hero-banner";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { LinksGrid } from "src/website-apps/shared/components/LinksGrid";
import { useMegaSearchContext } from "src/website-apps/shared/components/mega-search";
import { UserEventToastOnAncillaryPages } from "src/website-apps/shared/components/user-event-toast";
import { PrimaryRadiusInMeters } from "src/website-apps/shared/constants/grid-radius";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { getDateQueryParams } from "src/website-apps/shared/utils/getDateQueryParams";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import { BodyWrapper, MarginDivLarge, VerticalMarginRow } from "../styled-components";

const resolveInitialFavoritesState = (model: VenueEventListViewModel) => {
  const events = [
    ...Object.values(model.eventGrids).flatMap((payload) => payload.items ?? []),
    ...(model.trendingEvents?.items ?? []),
  ];
  const eventFavorites = Object.fromEntries(events.map((curr) => [curr.eventId ?? -1, curr.isFavorite]));
  return { [ENTITY_TYPES.EVENT]: { ...eventFavorites } };
};

interface Props {
  dates?: Maybe<Dates>;
}

export const VenueLayoutV1: FC<Props> = ({ dates }) => {
  const { search } = useWindowLocation();
  const now = useStableNow();
  const appContext = useAppContext();
  const { env } = appContext;
  const model = useVenuePageModelData();
  const initialFavorites = resolveInitialFavoritesState(model);
  const megaSearchContext = useMegaSearchContext();
  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const [location] = useLocationContext(cookieOptions);
  const execDateWithExpiration = useFeatureTernary(FeatureSetEnum.EventListExpireDateFilter);
  const immersiveHeaderOnAncillary = useFeatureTernary(FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages);
  const showImmersiveHeader =
    immersiveHeaderOnAncillary({
      control: () => false,
      variant: () => true,
      record: false,
    }) ?? false;
  const dateCookieOptions = useDateCookieOptions();
  const dateWithExpiration =
    execDateWithExpiration({
      control: () => false,
      variant: () => true,
    }) ?? undefined;
  const datesQueryParams = getDateQueryParams(env, search, now, dateWithExpiration, dateCookieOptions);
  const filters = getFilterQueryParams(search);

  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalTrendingOnAncillaryPages)();

  const reducerOptions = useMemo(
    () => ({
      primaryGrid: model.eventGrids[GridType.PrimaryRadius],
      primaryFromRadius: 0,
      primaryToRadius: PrimaryRadiusInMeters,
      restGrid: model.eventGrids[GridType.Rest],
      restFromRadius: PrimaryRadiusInMeters,
      restToRadius: null,
      method: "GetFilteredEvents",
      venueId: model.venueId,
      location,
      dates: datesQueryParams,
      sortBy: filters.sortBy,
      gridFilterType: model.isParkingVenue ? FilterTypes.PARKING : filters.gridFilterType,
      eventViewType: filters.eventViewType,
      calendarEvents: model.calendarEvents,
    }),
    [
      datesQueryParams,
      filters.eventViewType,
      filters.gridFilterType,
      filters.sortBy,
      location,
      model.calendarEvents,
      model.eventGrids,
      model.isParkingVenue,
      model.venueId,
    ],
  );
  const gridsContext = useGridsReducer(reducerOptions);

  const heroBannerFavoritesDataContext = useMemo(
    () => ({
      entityType: ENTITY_TYPES.VENUE,
      entityId: model.venueId,
    }),
    [model.venueId],
  );

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <EventsGridContext.Provider value={gridsContext}>
        <FavoritesProvider data={initialFavorites}>
          <HeroBannerFavoritesDataContext.Provider value={heroBannerFavoritesDataContext}>
            <HeadingLevel>
              <Layout
                header={
                  <MegaSearchProvider>
                    <HeadingLevel>
                      <NewImmersiveHeaderWrapper
                        venueId={model.venueId}
                        headerTitle={model.venueName}
                        dates={dates}
                        location={location}
                      >
                        <ErrorMessage />
                        {!appContext.isNativeApp && !megaSearchContext.active && (
                          <MarketplaceDisclosure
                            marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure}
                          />
                        )}
                        <HeaderConfiguration.Browse
                          discoveryLinks={model.header.navigationBarLinks}
                          isUserLoggedIn={appContext.isUserLoggedIn}
                          onMobileMenuToggle={handleOnMobileMenuToggle}
                          mobileMenuOpen={mobileMenuOpen}
                          bigPerformerBanner={showImmersiveHeader}
                        />
                      </NewImmersiveHeaderWrapper>
                    </HeadingLevel>
                  </MegaSearchProvider>
                }
                body={
                  <>
                    <UserEventToastOnAncillaryPages userEventOption={model.userEventOption} />
                    <BodyWrapper>
                      <Container>
                        <Row between={[true]}>
                          <Col span={[12]}>
                            <FiltersAndGridsManager filterTypeOptions={model.gridFilterTypes} />
                            {!!model.venuesWithinRadius?.items?.length && (
                              <MarginDivLarge>
                                <LinksGridManager
                                  title={<FormatContent id="cid$#1812011#" params={model.venueName} />}
                                  initLinks={model.venuesWithinRadius.items ?? []}
                                  venueId={model.venueId}
                                  method="GetVenueLinks"
                                  totalCount={model.venuesWithinRadius.totalCount ?? 0}
                                />
                              </MarginDivLarge>
                            )}
                            {model.performersAtVenue && model.performersAtVenue.length > 0 && (
                              <MarginDivLarge>
                                <LinksGrid
                                  title={<FormatContent id="cid$#1812008#" params={model.venueName} />}
                                  links={model.performersAtVenue}
                                />
                              </MarginDivLarge>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <EventListRecentlyViewedGrid />
                        </Row>
                        <VerticalMarginRow>
                          {!appContext.isNativeApp && (
                            <Col span={[12]}>
                              <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
                            </Col>
                          )}
                        </VerticalMarginRow>
                      </Container>
                    </BodyWrapper>
                  </>
                }
                footer={
                  <LazyFooter
                    companyLinks={model.footer.companyLinks}
                    supportLinks={model.footer.supportLinks}
                    getLocationSettingsUrl={model.footer.getLocationSettingsUrl}
                  />
                }
              />
            </HeadingLevel>
          </HeroBannerFavoritesDataContext.Provider>
        </FavoritesProvider>
      </EventsGridContext.Provider>
    </div>
  );
};
